import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { doc, getDoc, collection, addDoc, updateDoc, increment, query, where, getDocs, setDoc, writeBatch } from 'firebase/firestore';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, fetchSignInMethodsForEmail } from 'firebase/auth';
import { auth, db, functions } from '@/lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import Header from '@/components/layout/Header';
import { EventPageBackground } from '@/components/layout/EventPageBackground';
import { formatDiningDocumentTitle } from '@/components/layout/PageWithTitle';
import { formatDate, formatCurrency } from '@/lib/utils';
import { showSuccess, showError, showWarning, confirmAction } from '@/lib/swal';
import { buildInvoicePaymentLink } from '@/lib/payment-links';
import { generateUnitInvoiceNumber } from '@/lib/invoice-utils';
import { optionalRsvpUserIdFields } from '@/lib/rsvp-submit-policy';
import { generateGuestRsvpToken, hashGuestRsvpToken, storeGuestRsvpToken, readStoredGuestRsvpToken } from '@/lib/guest-rsvp-token';
import { Calendar, MapPin, PoundSterling, CheckCircle, Users, Copy, Share2, Lock } from 'lucide-react';
import { Event, GuestListEntry, GuestSubscription, RSVP } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { useRanks } from '@/hooks/useRanks';
import { VenueInfo } from '@/components/venues/VenueInfo';
import { UnitType } from '@/types';
import { getAvailablePrefixes, getRankAndShortName } from '@/lib/rank-utils';
import {
  mergeSingleGuestTicketIntoResponses,
  validateSingleGuestTicketSelections,
} from '@/lib/rsvp-single-guest-ticket';
import {
  getInviterAccessibleTicketsWithFallback,
} from '@/lib/inviter-accessible-tickets';
import { formatRsvpFirstName, formatRsvpLastName } from '@/lib/format-rsvp-names';
import {
  BOOKING_FEE_LABEL,
  getInvoiceCreditRecoveryPounds,
  shouldShowBookingFeeOnRsvp,
  isCreditRecoveryEnabledForEvent,
} from '@/lib/credit-booking-fee';
import { DEFAULT_CREDIT_BOOKING_FEE_PENCE, getCreditBookingFeePenceForEvent } from '@/lib/platform-booking-fee';
import {
  INVITE_LINK_QUERY_PARAM,
  resolveInviteParamsFromLinkDoc,
  type ResolvedInviteLinkParams,
} from '@/lib/personal-invite-links';
import {
  resolveEffectiveInvitedBy,
  computeInviterPaysForGuest,
  buildInviteFieldsForRsvpSubmit,
} from '@/lib/invite-link-rsvp-policy';
import { findFirstActiveRsvpDoc } from '@/lib/event-utils';

export default function RSVPPage() {
  const { unitId: unitId, eventId } = useParams<{ unitId: string; eventId: string }>();
  const [searchParams] = useSearchParams();
  const inviteLinkParam = searchParams.get(INVITE_LINK_QUERY_PARAM);
  const invitedByParam = searchParams.get('invitedBy');
  const ticketsParam = searchParams.get('tickets'); // Comma-separated ticket IDs
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { ranks, loading: ranksLoading } = useRanks();
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [currentUnit, setCurrentUnit] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submissionType, setSubmissionType] = useState<'standard' | 'apology' | null>(null);
  const [event, setEvent] = useState<Event | null>(null);
  const [existingRsvp, setExistingRsvp] = useState<any | null>(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [shareName, setShareName] = useState('');
  const [sharing, setSharing] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [emailHasAccount, setEmailHasAccount] = useState(false);

  /** When using invitedBy, resolved inviter RSVP for ticket scope; null if not found; undefined before fetch */
  const [inviterRsvpForTicketScope, setInviterRsvpForTicketScope] = useState<RSVP | null | undefined>(undefined);
  /** Loaded from rsvpLinks when inviteLink query param is present; undefined before fetch */
  const [resolvedInviteLink, setResolvedInviteLink] = useState<ResolvedInviteLinkParams | null | undefined>(undefined);

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const emailRef = useRef(email);
  emailRef.current = email;
  const [password, setPassword] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [rank, setRank] = useState('');
  const [additionalRank, setAdditionalRank] = useState('');
  const [unitName, setUnitName] = useState('');
  const [allergies, setAllergies] = useState('');
  const [futureEvents, setFutureEvents] = useState(false);
  // Ticket selections - for invited guests, only one ticket is selected
  const [selectedTicketId, setSelectedTicketId] = useState<string>('');
  // ticketCourseSelections[ticketId][courseId] = option (for single ticket)
  const [ticketCourseSelections, setTicketCourseSelections] = useState<Record<string, string>>({});
  // Questionnaire responses - keyed by question ID
  const [questionnaireResponses, setQuestionnaireResponses] = useState<Record<string, string | string[]>>({});
  /** True only after profile load when the account already had first, last, and email; never flips true while the user types */
  const [lockPersonalDetailsFromAccount, setLockPersonalDetailsFromAccount] = useState(false);

  const invitedBy = useMemo(
    () => resolveEffectiveInvitedBy(resolvedInviteLink, invitedByParam),
    [resolvedInviteLink, invitedByParam],
  );
  const inviterPaysForGuest = computeInviterPaysForGuest(invitedBy, resolvedInviteLink);

  const creditBookingFeePence = useMemo(() => getCreditBookingFeePenceForEvent(event), [event]);

  const currentUnitType = currentUnit ? unitTypes.find((ut) => ut.id === currentUnit.typeId) : undefined;
  const availablePrefixes = useMemo(
    () => getAvailablePrefixes(event, currentUnitType),
    [event, currentUnitType],
  );
  const hasConfiguredPrefixes = availablePrefixes.length > 0;
  const unitLabel = currentUnitType?.entityName || 'Unit/Organization';
  const requireAffiliationInfo = event?.requirePrefixRankUnitInfo ?? true;
  const allowFutureEventsInterest = event?.allowFutureEventsInterest ?? true;
  const optInToFutureEvents = allowFutureEventsInterest && futureEvents;

  useEffect(() => {
    if (!event) return;
    const segment = event.name?.trim() || 'RSVP';
    const next = formatDiningDocumentTitle(segment);
    if (typeof document !== 'undefined' && document.title !== next) {
      document.title = next;
    }
  }, [event]);

  // Filter tickets: URL list, inviter-accessible scope, or all (public RSVP)
  const availableTickets = useMemo(() => {
    if (!event?.tickets || event.tickets.length === 0) {
      return [];
    }

    const urlTicketIds = ticketsParam
      ? ticketsParam.split(',').map((id) => id.trim()).filter(Boolean)
      : [];

    if (invitedBy?.trim()) {
      if (inviterRsvpForTicketScope === undefined) {
        return [];
      }
      const base = getInviterAccessibleTicketsWithFallback(event.tickets, inviterRsvpForTicketScope);
      if (urlTicketIds.length > 0) {
        return base.filter((t) => urlTicketIds.includes(t.id));
      }
      return base;
    }

    if (urlTicketIds.length > 0) {
      return event.tickets.filter((ticket) => urlTicketIds.includes(ticket.id));
    }

    return event.tickets;
  }, [event?.tickets, ticketsParam, invitedBy, inviterRsvpForTicketScope]);

  // Fetch unit types
  useEffect(() => {
    const fetchUnitTypes = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
        if (settingsDoc.exists()) {
          const data = settingsDoc.data();
          setUnitTypes(data.unitTypes || []);
        }
      } catch (error) {
        console.error('Error fetching unit types:', error);
      }
    };

    fetchUnitTypes();
  }, []);

  // Fetch current unit
  useEffect(() => {
    const fetchCurrentUnit = async () => {
      if (!unitId) return;
      
      try {
        const unitDoc = await getDoc(doc(db, 'units', unitId));
        if (unitDoc.exists()) {
          setCurrentUnit({
            id: unitDoc.id,
            ...unitDoc.data()
          });
        }
      } catch (error) {
        console.error('Error fetching current unit:', error);
      }
    };

    fetchCurrentUnit();
  }, [unitId]);

  useEffect(() => {
    const fetchEvent = async () => {
      if (!unitId || !eventId) {
        showError('Invalid event link');
        setLoading(false);
        return;
      }

      try {
        setInviterRsvpForTicketScope(invitedByParam?.trim() ? undefined : null);
        setResolvedInviteLink(inviteLinkParam?.trim() ? undefined : null);

        const eventDoc = await getDoc(doc(db, 'units', unitId, 'events', eventId));
        if (!eventDoc.exists()) {
          showError('Event not found');
          setLoading(false);
          return;
        }

        const data = eventDoc.data();
        
        // Check if event is deleted
        if (data.eventStatus === 'deleted') {
          showError('This event has been deleted and is no longer available for RSVPs.');
          setLoading(false);
          return;
        }

        // Multi-tenant events use a dedicated bulk-quantity ticket purchase page.
        if (data.ticketMode === 'multiTenant') {
          navigate(`/unit/${unitId}/event/${eventId}/rsvp-tickets${location.search}`, { replace: true });
          return;
        }

        const eventDate = data.date?.toDate() || new Date();
        setEvent({
          id: eventDoc.id,
          unitId: unitId,
          name: data.name,
          ceremonyType: data.ceremonyType,
          date: eventDate,
          location: data.location,
          questionnaire: data.questionnaire || [],
          courses: data.courses || [],
          tickets: data.tickets || [],
          maxCapacity: data.maxCapacity,
          currentAttendees: data.currentAttendees || 0,
          waitlistEnabled: data.waitlistEnabled || false,
          waitlist: data.waitlist || [],
          rsvpDeadline: data.rsvpDeadline?.toDate() || new Date(),
          paymentDeadline: data.paymentDeadline?.toDate() || (data.rsvpDeadline?.toDate() || eventDate),
          eventStatus: data.eventStatus,
          dresscode: data.dresscode,
          customInstructions: data.customInstructions,
          isPublic: data.isPublic ?? false,
          requirePrefixRankUnitInfo: data.requirePrefixRankUnitInfo ?? true,
          allowFutureEventsInterest: data.allowFutureEventsInterest ?? true,
          useCustomPrefixes: Boolean(data.useCustomPrefixes),
          customPrefixes: Array.isArray(data.customPrefixes)
            ? data.customPrefixes.filter((p: unknown) => typeof p === 'string' && p.trim() !== '')
            : [],
          logoUrl: data.logoUrl,
          bannerUrl: data.bannerUrl,
          backgroundImageUrl: data.backgroundImageUrl,
          backgroundColor: data.backgroundColor,
          createdBy: data.createdBy,
          createdAt: data.createdAt?.toDate() || new Date(),
          guestCoversCreditCost: data.guestCoversCreditCost ?? false,
          creditBookingFeePence:
            typeof data.creditBookingFeePence === 'number' ? data.creditBookingFeePence : undefined,
          creditCostRecovery:
            data.creditCostRecovery &&
            typeof data.creditCostRecovery === 'object' &&
            !Array.isArray(data.creditCostRecovery)
              ? {
                  enabled: Boolean((data.creditCostRecovery as { enabled?: boolean }).enabled),
                  amountPence:
                    typeof (data.creditCostRecovery as { amountPence?: unknown }).amountPence === 'number'
                      ? (data.creditCostRecovery as { amountPence: number }).amountPence
                      : 60,
                }
              : { enabled: false, amountPence: DEFAULT_CREDIT_BOOKING_FEE_PENCE },
        });

        let loadedInviteLink: ResolvedInviteLinkParams | null = null;
        if (inviteLinkParam?.trim()) {
          try {
            const linkRef = doc(
              db,
              'units',
              unitId,
              'events',
              eventId,
              'rsvpLinks',
              inviteLinkParam.trim(),
            );
            const linkSnap = await getDoc(linkRef);
            if (linkSnap.exists()) {
              loadedInviteLink = resolveInviteParamsFromLinkDoc(inviteLinkParam.trim(), linkSnap.data());
              try {
                await updateDoc(linkRef, { clickCount: increment(1) });
              } catch (clickError) {
                console.error('Error tracking invite link click:', clickError);
              }
            }
          } catch (linkError) {
            console.error('Error loading invite link:', linkError);
          }
        }
        setResolvedInviteLink(inviteLinkParam?.trim() ? loadedInviteLink : null);

        const inviterKeyForScope = (loadedInviteLink?.invitedBy || invitedByParam)?.trim();

        if (inviterKeyForScope) {
          const inviterKey = inviterKeyForScope;
          let loadedInviter: RSVP | null = null;
          try {
            // The inviter's RSVP isn't readable by the invitee; the server returns just the
            // restricted ticket ids the inviter may extend (booked tickets + ticketFilter).
            const getInviterTicketScope = httpsCallable<
              { unitId: string; eventId: string; inviterKey: string },
              { found: boolean; ticketIds: string[] }
            >(functions, 'getInviterTicketScope');
            const { data: scope } = await getInviterTicketScope({ unitId, eventId, inviterKey });
            if (scope.found) {
              loadedInviter = { id: inviterKey, ticketFilter: scope.ticketIds.join(',') } as RSVP;
            }
          } catch (inviterErr) {
            console.error('Error loading inviter RSVP for ticket scope', inviterErr);
            loadedInviter = null;
          }
          setInviterRsvpForTicketScope(loadedInviter);
        }

        // Track link click by ticket match only when inviteLink param is absent (legacy links)
        if (ticketsParam && !inviteLinkParam?.trim()) {
          try {
            const ticketIds = ticketsParam.split(',').map(id => id.trim()).sort();
            const linksRef = collection(db, 'units', unitId, 'events', eventId, 'rsvpLinks');
            const linksSnapshot = await getDocs(linksRef);
            
            // Find matching link by comparing ticket IDs (sorted)
            let matchingLinkId: string | null = null;
            linksSnapshot.forEach((linkDoc) => {
              const linkData = linkDoc.data();
              const linkTicketIds = (linkData.ticketIds || []).slice().sort();
              if (linkTicketIds.length === ticketIds.length &&
                  linkTicketIds.every((id: string, index: number) => id === ticketIds[index])) {
                matchingLinkId = linkDoc.id;
              }
            });

            if (matchingLinkId) {
              // Increment click count
              const linkRef = doc(db, 'units', unitId, 'events', eventId, 'rsvpLinks', matchingLinkId);
              await updateDoc(linkRef, {
                clickCount: increment(1),
              });
            }
          } catch (error) {
            // Silently fail - link tracking is not critical
            console.error('Error tracking link click:', error);
          }
        }


        // Check if user has already RSVP'd for this event (logged in or by email)
        if (currentUser) {
          // First, check by userId (primary method for logged-in users)
          const rsvpsByUserIdQuery = query(
            collection(db, 'units', unitId, 'events', eventId, 'rsvps'),
            where('userId', '==', currentUser.uid)
          );
          const rsvpsByUserIdSnapshot = await getDocs(rsvpsByUserIdQuery);
          
          const activeRsvpByUserId = findFirstActiveRsvpDoc(rsvpsByUserIdSnapshot.docs);
          if (activeRsvpByUserId && activeRsvpByUserId.data()?.status !== 'cancelled') {
            const rsvpId = activeRsvpByUserId.id;
            // Redirect logged-in users to their RSVP dashboard if they already have an RSVP
            navigate(`/unit/${unitId}/event/${eventId}/rsvp/${rsvpId}/dashboard`, { replace: true });
            return;
          }
          
          // Also check by email in case they created an RSVP before logging in
          if (currentUser.email) {
            const rsvpsByEmailQuery = query(
              collection(db, 'units', unitId, 'events', eventId, 'rsvps'),
              where('email', '==', currentUser.email.trim())
            );
            const rsvpsByEmailSnapshot = await getDocs(rsvpsByEmailQuery);
            
            const activeRsvpByEmail = findFirstActiveRsvpDoc(rsvpsByEmailSnapshot.docs);
            if (activeRsvpByEmail && activeRsvpByEmail.data()?.status !== 'cancelled') {
              const rsvpId = activeRsvpByEmail.id;
              const rsvpData = activeRsvpByEmail.data();

              // Update the RSVP with userId if it doesn't have one (link it to the user account)
              if (rsvpData && !rsvpData.userId) {
                try {
                  await updateDoc(activeRsvpByEmail.ref, {
                    userId: currentUser.uid,
                    updatedAt: new Date(),
                  });
                } catch (updateError) {
                  console.error('Error updating RSVP with userId:', updateError);
                  // Continue anyway - redirect will still work
                }
              }
              
              // Redirect logged-in users to their RSVP dashboard if they already have an RSVP
              navigate(`/unit/${unitId}/event/${eventId}/rsvp/${rsvpId}/dashboard`, { replace: true });
              return;
            }
          }
        }
      } catch (error) {
        console.error('Error fetching event:', error);
        showError('Failed to load event');
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [unitId, eventId, currentUser, navigate, location.key, invitedByParam, inviteLinkParam, ticketsParam]);

  // Non-logged-in: detect existing RSVP by email without re-running the full event fetch on every keystroke
  useEffect(() => {
    if (!unitId || !eventId) return;

    if (currentUser) {
      setExistingRsvp(null);
      return;
    }

    const trimmed = email.trim();
    if (!trimmed) {
      setExistingRsvp(null);
      return;
    }

    const debounceMs = 450;
    const handle = window.setTimeout(async () => {
      const emailAtFire = trimmed;
      try {
        // Guests aren't authenticated, so rsvps can't be queried directly (see firestore.rules
        // comments on the rsvps subcollection) — this goes through a token-free Admin SDK lookup
        // keyed by the public rsvpEmailIndex instead.
        const checkRsvpEmailStatusCallable = httpsCallable(functions, 'checkRsvpEmailStatus');
        const result = await checkRsvpEmailStatusCallable({ unitId, eventId, email: emailAtFire });
        const status = result.data as { exists: boolean; id?: string; status?: string | null; active?: boolean };

        if (emailRef.current.trim() !== emailAtFire) {
          return;
        }

        if (status.exists && status.id && status.active) {
          setExistingRsvp({ id: status.id, status: status.status ?? null });
        } else {
          setExistingRsvp(null);
        }
      } catch (err) {
        console.error('Error checking RSVP by email:', err);
      }
    }, debounceMs);

    return () => window.clearTimeout(handle);
  }, [unitId, eventId, currentUser, email]);

  // Pre-populate form fields for logged-in users
  useEffect(() => {
    if (!currentUser) {
      setLockPersonalDetailsFromAccount(false);
      return;
    }

    // Wait for ranks to finish loading (not for a non-empty list) so rankId can be matched,
    // without blocking name/email prefill when no ranks are configured.
    if (!ranksLoading) {
      // Pre-populate from auth
      setEmail(currentUser.email || '');

      // Fetch user profile to get rank and unit preferences
      const fetchUserProfile = async () => {
        try {
          if (currentUser.email) {
            setEmail(currentUser.email);
          }

          const nextEmail = currentUser.email || '';
          let nextFirst = '';
          let nextLast = '';

          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            if (userData.firstName) {
              nextFirst = userData.firstName;
            } else if (userData.name) {
              const parts = userData.name.split(' ');
              nextFirst = parts[0] || '';
            }
            if (userData.lastName) {
              nextLast = userData.lastName;
            } else if (userData.name) {
              const parts = userData.name.split(' ');
              nextLast = parts.slice(1).join(' ') || '';
            }

            // Pre-select rank from primaryUnits (new structure) or fall back to legacy rank field
            if (userData.primaryUnits && Array.isArray(userData.primaryUnits) && userData.primaryUnits.length > 0) {
              const primaryUnit = userData.primaryUnits[0];
              if (primaryUnit.rankId) {
                const matchedRank = ranks.find(r => r.id === primaryUnit.rankId);
                if (matchedRank) {
                  setRank(matchedRank.name);
                }
              }
              if (primaryUnit.rank) {
                setAdditionalRank(primaryUnit.rank);
              }
              if (primaryUnit.unitName) {
                setUnitName(primaryUnit.unitName);
              }
            } else {
              if (userData.rank) {
                setRank(userData.rank);
              }
              if (userData.unitName) {
                setUnitName(userData.unitName);
              }
            }
          }

          if ((!nextFirst || !nextLast) && currentUser.displayName) {
            const parts = currentUser.displayName.trim().split(/\s+/);
            if (!nextFirst) nextFirst = parts[0] || '';
            if (!nextLast) nextLast = parts.slice(1).join(' ');
          }

          setFirstName(nextFirst);
          setLastName(nextLast);
          setLockPersonalDetailsFromAccount(
            nextFirst.trim() !== '' &&
              nextLast.trim() !== '' &&
              nextEmail.trim() !== '',
          );
        } catch (error) {
          console.error('Error fetching user profile:', error);
          setLockPersonalDetailsFromAccount(false);
        }
      };

      fetchUserProfile();
    }
  }, [currentUser, ranks, ranksLoading]);

  const isInterestOnly = event?.eventStatus === 'interest-only';
  const isEventLocked = event?.eventStatus === 'closed';
  const hasRegisteredInterest = isInterestOnly && existingRsvp && existingRsvp.status === 'waitlist';

  // Auto-select ticket if only one is available
  useEffect(() => {
    if (event && availableTickets.length === 1 && !isInterestOnly) {
      const singleTicket = availableTickets[0];
      // Only auto-select if no ticket is selected, or if the selected ticket doesn't belong to available tickets
      if (!selectedTicketId || !availableTickets.find(t => t.id === selectedTicketId)) {
        setSelectedTicketId(singleTicket.id);
      }
    }
  }, [event, availableTickets, selectedTicketId, isInterestOnly]);

  // Check if email has an existing account
  const checkEmailExists = async (emailToCheck: string): Promise<boolean> => {
    try {
      const methods = await fetchSignInMethodsForEmail(auth, emailToCheck);
      return methods.length > 0;
    } catch (error) {
      console.error('Error checking email:', error);
      return false;
    }
  };

  // Handle email blur - check if account exists
  const handleEmailBlur = async () => {
    if (email && !currentUser) {
      const exists = await checkEmailExists(email);
      setEmailHasAccount(exists);
      if (exists) {
        setShowLoginPrompt(true);
      }
    }
  };

  // Handle login for existing users
  const handleLogin = async () => {
    if (!loginPassword) {
      showError('Please enter your password');
      return;
    }

    try {
      await signInWithEmailAndPassword(auth, email, loginPassword);
      showSuccess('Logged in successfully!');
      setShowLoginPrompt(false);
      setLoginPassword('');
      setEmailHasAccount(false);
    } catch (error: any) {
      console.error('Login error:', error);
      if (error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found') {
        showError('Incorrect password. Please try again.');
      } else {
        showError('Failed to log in. Please try again.');
      }
    }
  };

  const handleUnregisterInterest = async () => {
    if (!existingRsvp || !unitId || !eventId) return;

    const result = await confirmAction({
      title: 'Unregister Interest?',
      text: 'Are you sure you want to remove your interest registration for this event?',
      confirmButtonText: 'Yes, Unregister',
    });

    if (!result.isConfirmed) return;

    // Only reachable for guest (non-logged-in) interest registrations. Guests can't delete rsvps
    // directly anymore (requires isAuthenticated() — see firestore.rules), so this goes through the
    // token-verified cancelGuestRsvp callable instead, using the capability token stashed in this
    // browser's session storage when the interest registration was created.
    const token = readStoredGuestRsvpToken(existingRsvp.id);
    if (!token) {
      showError('We can\'t verify this registration on this device. Please use the link from your confirmation email instead.');
      return;
    }

    setSubmitting(true);
    try {
      const cancelGuestRsvpCallable = httpsCallable(functions, 'cancelGuestRsvp');
      await cancelGuestRsvpCallable({ unitId, eventId, rsvpId: existingRsvp.id, token });
      setExistingRsvp(null);
      showSuccess('Your interest registration has been removed.');
    } catch (error) {
      console.error('Error unregistering interest:', error);
      showError('Failed to unregister interest. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyInterestLink = async () => {
    if (!event) return;
    
    const interestLink = `${window.location.origin}/unit/${unitId}/event/${eventId}/rsvp`;
    
    try {
      await navigator.clipboard.writeText(interestLink);
      showSuccess('Interest registration link copied to clipboard!');
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      showError(`Failed to copy. Link: ${interestLink}`);
    }
  };

  const handleShareInterest = async () => {
    if (!shareEmail.trim() || !shareName.trim()) {
      showError('Please enter both name and email address');
      return;
    }

    if (!event || !unitId || !eventId) return;

    setSharing(true);
    try {
      const interestLink = `${window.location.origin}/unit/${unitId}/event/${eventId}/rsvp`;
      
      // Fetch venue data if venueId exists
      let venueData = null;
      if (event.venueId && unitId) {
        try {
          const venueDoc = await getDoc(doc(db, 'units', unitId, 'venues', event.venueId));
          if (venueDoc.exists()) {
            venueData = venueDoc.data();
          }
        } catch (venueError) {
          console.log('Could not fetch venue data:', venueError);
        }
      }
      
      const sendShareEmail = httpsCallable(functions, 'sendShareInterestEmail');
      await sendShareEmail({
        to: shareEmail,
        recipientName: shareName,
        eventName: event.name,
        eventDate: event.date,
        eventLocation: event.location,
        eventVenue: venueData,
        interestLink: interestLink,
        sharedBy: currentUser?.displayName || (firstName && lastName ? `${firstName} ${lastName}` : '') || 'A friend'
      });

      showSuccess(`Interest link sent to ${shareName}!`);
      setShareEmail('');
      setShareName('');
      setShowShareDialog(false);
    } catch (error) {
      console.error('Error sharing interest:', error);
      showError('Failed to share interest link. Please try again.');
    } finally {
      setSharing(false);
    }
  };

  const submitRsvp = async (apology = false) => {
    if (submitting) {
      return;
    }

    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      showError('Please fill in all required fields');
      return;
    }

    // Skip detailed validation for interest-only mode or apologies
    if (!isInterestOnly && !apology) {
      const ticketValidationError = validateSingleGuestTicketSelections(
        availableTickets,
        selectedTicketId,
        ticketCourseSelections,
      );
      if (ticketValidationError) {
        showError(ticketValidationError);
        return;
      }
    }

    if (!event || !unitId || !eventId) {
      showError('Invalid event');
      return;
    }

    if (event.eventStatus === 'closed') {
      showError('This event is locked. New RSVPs are no longer being accepted.');
      return;
    }

    if (!currentUser && emailHasAccount) {
      setShowLoginPrompt(true);
      showError('This email is already registered. Please log in to continue.');
      return;
    }

    if (!apology && event.maxCapacity && event.currentAttendees >= event.maxCapacity && !isInterestOnly) {
      showError('This event is at full capacity. You will be added to the waitlist.');
    }

    if (!apology && !isInterestOnly && requireAffiliationInfo) {
      if (!hasConfiguredPrefixes) {
        showError('This unit has no configured prefixes. Please contact the event organizer.');
        return;
      }
      if (!rank.trim()) {
        showError('Please select your prefix.');
        return;
      }
    }


    // Validate required questionnaire items
    if (!isInterestOnly && !apology && event?.questionnaire) {
      for (const question of event.questionnaire) {
        if (question.required) {
          const response = questionnaireResponses[question.id];
          if (!response || (typeof response === 'string' && !response.trim()) || (Array.isArray(response) && response.length === 0)) {
            showError(`Please answer the required question: ${question.question}`);
            return;
          }
        }
      }
    }

    setSubmissionType(apology ? 'apology' : 'standard');
    setSubmitting(true);

    try {
      const latestEventDoc = await getDoc(doc(db, 'units', unitId, 'events', eventId));
      if (!latestEventDoc.exists() || latestEventDoc.data()?.eventStatus === 'closed') {
        showError('This event was locked before your RSVP could be submitted.');
        return;
      }

      const userId = currentUser?.uid;
      const formattedFirstName = formatRsvpFirstName(firstName);
      const formattedLastName = formatRsvpLastName(lastName);

      const responses: Record<string, any> = !isInterestOnly && !apology
        ? {
            Allergies: allergies,
          }
        : {};

      // Add questionnaire responses
      if (!isInterestOnly && !apology && event?.questionnaire) {
        event.questionnaire.forEach((question) => {
          const response = questionnaireResponses[question.id];
          if (response !== undefined && response !== null && response !== '') {
            if (Array.isArray(response) && response.length > 0) {
              responses[question.question] = response;
            } else if (typeof response === 'string' && response.trim()) {
              responses[question.question] = response;
            }
          }
        });
      }

      if (!isInterestOnly && !apology) {
        mergeSingleGuestTicketIntoResponses(
          responses,
          availableTickets,
          selectedTicketId,
          ticketCourseSelections,
        );
      }

      const rsvpData: any = {
        eventId,
        firstName: formattedFirstName,
        lastName: formattedLastName,
        name: `${formattedFirstName} ${formattedLastName}`,
        // Stored lower-case to match the email index key and sign-in emails.
        email: email.trim().toLowerCase(),
        responses,
        status: apology
          ? 'declined'
          : isInterestOnly
            ? 'waitlist'
            : event.maxCapacity && event.currentAttendees >= event.maxCapacity
              ? 'waitlist'
              : 'confirmed',
        apologies: apology,
        checkedIn: false,
        plusOneAllowed: false,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      Object.assign(rsvpData, optionalRsvpUserIdFields(userId));
      if (!isInterestOnly || apology) {
        const { rank: resolvedRank, rankShortName } = getRankAndShortName({
          rankName: rank,
          currentUnitType,
          globalRanks: ranks,
        });

        if (resolvedRank) {
          rsvpData.rank = resolvedRank;
        }

        if (rankShortName !== null) {
          rsvpData.rankShortName = rankShortName;
        } else {
          rsvpData.rankShortName = null;
        }
      }
      if ((!isInterestOnly || apology) && additionalRank) {
        rsvpData.additionalRank = additionalRank;
      }
      if ((!isInterestOnly || apology) && unitName) {
        rsvpData.unit = unitName;
      }

      Object.assign(rsvpData, buildInviteFieldsForRsvpSubmit(invitedBy, resolvedInviteLink));

      // Store ticket filter if user accessed via a filtered link
      if (ticketsParam) {
        (rsvpData as any).ticketFilter = ticketsParam;
      }

      // Server-side duplicate prevention using email index
      const normalizedEmail = email.trim().toLowerCase();
      const rsvpRef = doc(collection(db, 'units', unitId, 'events', eventId, 'rsvps'));
      const emailIndexRef = doc(db, 'units', unitId, 'events', eventId, 'rsvpEmailIndex', normalizedEmail);
      
      // Duplicate check — a cancelled prior booking should not block re-registration. The email
      // index isn't client-readable (it would reveal who's booked), so this goes through the
      // checkRsvpEmailStatus callable for everyone.
      {
        const checkRsvpEmailStatusCallable = httpsCallable(functions, 'checkRsvpEmailStatus');
        const result = await checkRsvpEmailStatusCallable({ unitId, eventId, email: normalizedEmail });
        const status = result.data as { exists: boolean; id?: string; status?: string | null; active?: boolean };

        if (status.exists && status.status !== 'cancelled') {
          if (status.id && status.active) {
            showError('You have already registered for this event. Please check your email for your RSVP confirmation.');
            navigate(`/unit/${unitId}/event/${eventId}/rsvp/${status.id}/dashboard`);
            return;
          }
          showError('You have already registered for this event. Please check your email for your RSVP confirmation.');
          return;
        }
      }
      
      // Guests (no account) manage their booking with a capability token; only its SHA-256 hash is
      // stored on the RSVP. The raw token lives in the guest's link / session storage and is the
      // credential the getGuestRsvp/manageGuestRsvp/cancelGuestRsvp callables verify server-side.
      let guestManageToken: string | null = null;
      if (!userId) {
        guestManageToken = generateGuestRsvpToken();
        rsvpData.manageTokenHash = await hashGuestRsvpToken(guestManageToken);
        // Transient only: read once by the confirmation-email trigger to build a working dashboard
        // link, then deleted. Without this, the emailed link has no token and is a dead end.
        rsvpData.pendingGuestToken = guestManageToken;
      }

      // Self-paying bookings (guest or logged-in) create their invoice eagerly in the same batch so
      // the RSVP already carries its invoiceId. This removes the post-create RSVP/invoice updates —
      // which anonymous guests are no longer permitted to perform under the tightened rules.
      // Inviter-paid guests ride the host invoice; interest-only / apology bookings have no invoice.
      const createInvoiceNow = !isInterestOnly && !apology && !inviterPaysForGuest;

      let invoiceRef: ReturnType<typeof doc> | null = null;
      let invoiceData: Record<string, any> | null = null;
      if (createInvoiceNow) {
        invoiceRef = doc(collection(db, 'units', unitId, 'events', eventId, 'invoices'));
        const invoiceNumber = await generateUnitInvoiceNumber(db, unitId);

        let ticketPrice = 0;
        if (availableTickets.length > 0 && selectedTicketId) {
          const selectedTicket = availableTickets.find((t) => t.id === selectedTicketId);
          if (selectedTicket) {
            ticketPrice = selectedTicket.price || 0;
          }
        }

        const recoveryEnabled = isCreditRecoveryEnabledForEvent(event);
        const recoveryAmountPence = recoveryEnabled ? creditBookingFeePence : 0;
        const recoveryAmount = recoveryAmountPence / 100;
        // recovery only applies if ticket price > 0
        const actualRecoveryAmount = ticketPrice > 0 ? recoveryAmount : 0;
        const platformFeeTotal = actualRecoveryAmount * 1; // 1 RSVP per invoice here
        const totalAmount = ticketPrice + platformFeeTotal;

        invoiceData = {
          eventId,
          unitId,
          ownerUid: userId ?? '',
          totalAmount,
          amountPaid: 0,
          outstandingBalance: totalAmount,
          status: 'outstanding',
          rsvps: [rsvpRef.id],
          paymentHistory: [],
          paymentLink: buildInvoicePaymentLink(window.location.origin, invoiceRef.id),
          remindersSent: [],
          invoiceNumber,
          createdAt: new Date(),
          updatedAt: new Date(),
        };

        if (recoveryEnabled && ticketPrice > 0) {
          invoiceData.allInPriceBreakdown = {
            ticketPrice,
            creditCostRecovery: platformFeeTotal,
          };
          invoiceData.accountingTotals = {
            totalLodgeTransfer: ticketPrice,
            totalPlatformProfit: 0,
            platformFeeTotal,
          };
        }

        (rsvpData as any).invoiceId = invoiceRef.id;
      }

      // Create email index, RSVP and (optionally) invoice in one atomic batch.
      const batch = writeBatch(db);
      batch.set(emailIndexRef, {
        email: normalizedEmail,
        rsvpId: rsvpRef.id,
        createdAt: new Date(),
      });
      batch.set(rsvpRef, rsvpData);
      if (invoiceRef && invoiceData) {
        batch.set(invoiceRef, invoiceData);
      }
      await batch.commit();

      const creditsPerRsvpNum = event.creditsPerRsvp !== undefined ? Number(event.creditsPerRsvp) : 1;
      const needsFinalizeCredits =
        !isInterestOnly &&
        !apology &&
        creditsPerRsvpNum > 0 &&
        rsvpData.status === 'confirmed';

      if (needsFinalizeCredits) {
        const finalizeFn = httpsCallable(functions, 'finalizeRsvpCredits');
        try {
          await finalizeFn({ unitId, eventId, rsvpId: rsvpRef.id });
        } catch (err: unknown) {
          console.error('finalizeRsvpCredits failed (RSVP kept; lodge balance may go negative)', err);
          showWarning(
            'Your RSVP was recorded. The lodge credit balance could not be updated immediately; this is fine if the balance is low or negative. Contact the secretary if you need confirmation.',
          );
        }
      }

      // currentAttendees is recounted server-side (syncEventAttendeeCount trigger).

      if (userId && (!isInterestOnly || apology)) {
        const updates: any = {};
        if (optInToFutureEvents) {
          updates.futureMeetingsOptIn = true;
        }
        if (rank) {
          updates.rank = rank;
        }
        if (unitName) {
          updates.unitName = unitName;
        }
        if (Object.keys(updates).length > 0) {
          await updateDoc(doc(db, 'users', userId), updates);
        }
      }

      if (optInToFutureEvents && unitId) {
        try {
          const unitDoc = await getDoc(doc(db, 'units', unitId));
          if (unitDoc.exists()) {
            const unitData = unitDoc.data();
            const guestList = unitData.guestList || [];

            const existingGuest = guestList.find((guest: GuestListEntry) => guest.email === email);

            if (!existingGuest) {
              const guestEntry: GuestListEntry = {
                id: Math.random().toString(36).substring(2, 15),
                firstName: formattedFirstName,
                lastName: formattedLastName,
                email,
                addedAt: new Date(),
                source: 'rsvp',
              };
              if (userId) {
                guestEntry.userId = userId;
              }

              const updatedGuestList = [...guestList, guestEntry];
              await updateDoc(doc(db, 'units', unitId), {
                guestList: updatedGuestList,
              });

              if (userId) {
                const userDoc = await getDoc(doc(db, 'users', userId));
                if (userDoc.exists()) {
                  const userData = userDoc.data();
                  const existingSubscriptions = userData.guestSubscriptions || [];

                  const alreadySubscribed = existingSubscriptions.some(
                    (sub: GuestSubscription) => sub.unitId === unitId,
                  );

                  if (!alreadySubscribed) {
                    const guestSubscription: GuestSubscription = {
                      unitId,
                      subscribedAt: new Date(),
                    };
                    const updatedSubscriptions = [...existingSubscriptions, guestSubscription];
                    await updateDoc(doc(db, 'users', userId), {
                      guestSubscriptions: updatedSubscriptions,
                    });
                  }
                }
              }
            }
          }
        } catch (error) {
          console.error('Error adding to guest list:', error);
        }
      }

      const successMessage = apology
        ? 'Your apology has been recorded. We\'ll let the organizers know you cannot attend.'
        : isInterestOnly
          ? 'Interest registered successfully! We\'ll notify you when full details are available.'
          : 'RSVP submitted successfully!';

      showSuccess(successMessage);

      if (isInterestOnly) {
        navigate(`/unit/${unitId}/event/${eventId}/interest-confirmation`);
      } else {
        // Guests carry their capability token so the dashboard can load their booking via the
        // token-verified getGuestRsvp callable (RSVPs are no longer publicly readable).
        if (!currentUser && guestManageToken) {
          storeGuestRsvpToken(rsvpRef.id, guestManageToken);
          navigate(
            `/unit/${unitId}/event/${eventId}/rsvp/${rsvpRef.id}/dashboard?t=${encodeURIComponent(guestManageToken)}`,
          );
        } else {
          navigate(`/unit/${unitId}/event/${eventId}/rsvp/${rsvpRef.id}/dashboard`);
        }
      }
    } catch (error) {
      console.error('Error submitting RSVP:', error);
      showError('Failed to submit RSVP. Please try again.');
    } finally {
      setSubmitting(false);
      setSubmissionType(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitRsvp(false);
  };

  const handleApologySubmit = async () => {
    await submitRsvp(true);
  };

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading event...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <Card className="max-w-md">
            <CardHeader>
              <CardTitle>Event Not Found</CardTitle>
              <CardDescription>The event you're looking for doesn't exist or has been removed.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate('/')}>Return Home</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (isEventLocked) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center px-4">
          <Card className="max-w-md w-full">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
                <Lock className="h-8 w-8 text-amber-700" />
              </div>
              <h1 className="text-2xl font-semibold text-foreground">This Event is Locked</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                This event is no longer accepting new RSVPs.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <EventPageBackground
      backgroundImageUrl={event.backgroundImageUrl}
      backgroundColor={event.backgroundColor}
    >
      <Header />
      <main className="flex-1 px-3 py-8 sm:px-6">
        <div className="w-full sm:mx-auto sm:max-w-3xl">
          {/* Banner */}
          {event.bannerUrl && (
            <div className="mb-6 rounded-lg overflow-hidden">
              <img
                src={event.bannerUrl}
                alt="Event Banner"
                className="w-full h-auto object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          )}

          {/* Event Header */}
          <Card className="mb-6">
            <CardHeader>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex-1">
                  {/* Logo */}
                  {(event.logoUrl || currentUnit?.settings?.logoUrl) && (
                    <div className="mb-4">
                      <img
                        src={event.logoUrl || currentUnit?.settings?.logoUrl}
                        alt="Event Logo"
                        className="h-20 object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  
                  <CardTitle className="text-2xl">{event.name}</CardTitle>
                  {event.ceremonyType && (
                    <p className="text-sm text-primary font-medium mt-1">{event.ceremonyType}</p>
                  )}
                  <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                    <div className="flex items-center text-base">
                      <Calendar className="mr-2 h-4 w-4" />
                      {formatDate(event.date)}
                    </div>
                    <div className="flex items-start text-base">
                      <MapPin className="mr-2 h-4 w-4 mt-0.5 flex-shrink-0" />
                      <VenueInfo
                        unitId={unitId!}
                        venueId={event.venueId}
                        location={event.location}
                        compact={true}
                      />
                    </div>
                    <div className="flex items-center text-base">
                      <Calendar className="mr-2 h-4 w-4" />
                      RSVP deadline: {formatDate(event.rsvpDeadline)}
                    </div>
                    <div className="flex items-center text-base">
                      <Calendar className="mr-2 h-4 w-4" />
                      Payment deadline: {formatDate(event.paymentDeadline || event.rsvpDeadline)}
                    </div>
                    {availableTickets.length > 0 ? (
                      <div className="flex items-center text-base">
                        <PoundSterling className="mr-2 h-4 w-4" />
                        Ticketed event
                      </div>
                    ) : null}
                  </div>
                </div>
                <Badge variant={isInterestOnly ? 'secondary' : 'default'}>
                  {isInterestOnly 
                    ? 'Interest Registration Open' 
                    : `${event.currentAttendees}/${event.maxCapacity || '∞'} attending`
                  }
                </Badge>
              </div>
            </CardHeader>
          </Card>

          {/* Interest Already Registered Screen */}
          {hasRegisteredInterest ? (
            <>
              <Card className="border-green-200 bg-green-50">
                <CardContent className="pt-6">
                  <div className="text-center space-y-4">
                    <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="h-10 w-10 text-green-600" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-green-900 mb-2">Interest Registered!</h2>
                      <p className="text-green-800">
                        You have successfully registered your interest for this event.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>What's Next?</CardTitle>
                  <CardDescription>
                    Here's what will happen now
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold">
                        1
                      </div>
                      <div>
                        <h3 className="font-semibold">Event Planning</h3>
                        <p className="text-sm text-muted-foreground">
                          The organizers are finalizing details such as menu, pricing, and arrangements.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold">
                        2
                      </div>
                      <div>
                        <h3 className="font-semibold">Notification Email</h3>
                        <p className="text-sm text-muted-foreground">
                          You'll receive an email when the event is fully published with all details.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold">
                        3
                      </div>
                      <div>
                        <h3 className="font-semibold">Complete Your RSVP</h3>
                        <p className="text-sm text-muted-foreground">
                          Select meal preferences, provide dietary requirements, and confirm attendance.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t space-y-3">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <Button
                        variant="outline"
                        onClick={handleCopyInterestLink}
                      >
                        <Copy className="mr-2 h-4 w-4" />
                        Copy Link
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setShowShareDialog(true)}
                      >
                        <Share2 className="mr-2 h-4 w-4" />
                        Share
                      </Button>
                    </div>

                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={handleUnregisterInterest}
                      disabled={submitting}
                    >
                      {submitting ? 'Unregistering...' : 'Unregister Interest'}
                    </Button>

                    {currentUser ? (
                      <Button
                        className="w-full"
                        onClick={() => navigate('/dashboard')}
                      >
                        Return to Dashboard
                      </Button>
                    ) : (
                      <Card className="border-blue-200 bg-blue-50">
                        <CardContent className="pt-6">
                          <div className="text-center space-y-3">
                            <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                              <Users className="h-6 w-6 text-blue-600" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-blue-900">Create an Account</h3>
                              <p className="text-sm text-blue-800 mt-1">
                                Track your RSVPs, manage your profile, and get notified about future events.
                              </p>
                            </div>
                            <Button
                              className="w-full"
                              onClick={() => navigate('/signup', { state: { returnTo: location.pathname + location.search } })}
                            >
                              Create Free Account
                            </Button>
                            <p className="text-xs text-blue-700">
                              Already have an account?{' '}
                              <button
                                onClick={() => navigate('/login', { state: { returnTo: location.pathname + location.search } })}
                                className="underline hover:text-blue-900"
                              >
                                Log in
                              </button>
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              {/* Already RSVP'd Notice */}
              {!currentUser && (
                <Card className="mb-6 border-blue-200 bg-blue-50">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-3">
                      <div className="rounded-full bg-blue-100 p-2">
                        <Calendar className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-blue-900 mb-1">Already RSVP'd?</h3>
                        <p className="text-sm text-blue-800 mb-3">
                          If you've already registered for this event, please log in to view your RSVP dashboard.
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate('/login', { state: { returnTo: location.pathname + location.search } })}
                          className="bg-white hover:bg-blue-50"
                        >
                          Log In to View My RSVP
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* RSVP Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                {isInterestOnly && (
                  <div className="mb-6 rounded-lg bg-blue-50 p-4 border border-blue-200">
                    <h4 className="font-semibold text-blue-900 mb-2">Early Interest Registration</h4>
                    <p className="text-sm text-blue-800">
                      This event is currently accepting interest registrations. Once the organizers finalize all details 
                      (menu, pricing, etc.), you'll be notified to complete your full RSVP.
                    </p>
                  </div>
                )}
                {/* Simplified form for interest-only */}
                {isInterestOnly ? (
                  <>
                    <Card>
                      <CardHeader>
                        <CardTitle>Personal Details</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <div className="space-y-2">
                            <Label htmlFor="firstName">First Name *</Label>
                            <Input
                              id="firstName"
                              type="text"
                              placeholder="John"
                              value={firstName}
                              onChange={(e) => setFirstName(e.target.value)}
                              disabled={!!currentUser && lockPersonalDetailsFromAccount}
                              className={currentUser && lockPersonalDetailsFromAccount ? 'bg-muted cursor-not-allowed' : ''}
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="lastName">Last Name *</Label>
                            <Input
                              id="lastName"
                              type="text"
                              placeholder="Smith"
                              value={lastName}
                              onChange={(e) => setLastName(e.target.value)}
                              disabled={!!currentUser && lockPersonalDetailsFromAccount}
                              className={currentUser && lockPersonalDetailsFromAccount ? 'bg-muted cursor-not-allowed' : ''}
                              required
                            />
                          </div>
                        </div>
                        {currentUser && lockPersonalDetailsFromAccount && (
                          <p className="text-xs text-muted-foreground -mt-2">
                            Using name from your account
                          </p>
                        )}

                        <div className="space-y-2">
                          <Label htmlFor="email">Email Address *</Label>
                          <Input
                            id="email"
                            type="email"
                            placeholder="john@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={!!currentUser && lockPersonalDetailsFromAccount}
                            className={currentUser && lockPersonalDetailsFromAccount ? 'bg-muted cursor-not-allowed' : ''}
                            required
                          />
                          {currentUser && lockPersonalDetailsFromAccount && (
                            <p className="text-xs text-muted-foreground">
                              Using email from your account
                            </p>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {allowFutureEventsInterest && (
                      <Card>
                        <CardContent className="pt-6">
                          <label className="flex items-start space-x-3 cursor-pointer">
                            <input
                              type="checkbox"
                              id="future-events-interest"
                              checked={futureEvents}
                              onChange={(e) => setFutureEvents(e.target.checked)}
                              className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                            />
                            <div>
                              <span className="font-medium">Invite me to future events</span>
                              <p className="text-sm text-muted-foreground mt-0.5">
                                Stay informed about upcoming events from this unit
                              </p>
                            </div>
                          </label>
                        </CardContent>
                      </Card>
                    )}

                    <Button type="submit" className="w-full" disabled={submitting || isEventLocked}>
                      {submitting ? 'Registering...' : 'Register Interest'}
                    </Button>
                  </>
                ) : (
                  <>
                    {/* Personal Details Card */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Personal Details</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {requireAffiliationInfo && (
                          <div className="space-y-4">
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                              <div className="space-y-2">
                                <Label htmlFor="rank">Prefix</Label>
                                <Select value={rank} onValueChange={(value) => setRank(value)}>
                                  <SelectTrigger id="rank">
                                    <SelectValue placeholder="Select your prefix" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {availablePrefixes.map((prefix) => (
                                      <SelectItem key={prefix.id} value={prefix.name}>
                                        {prefix.shortName
                                          ? `${prefix.name} (${prefix.shortName})`
                                          : prefix.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="additionalRank">Rank</Label>
                                <Input
                                  id="additionalRank"
                                  type="text"
                                  placeholder="eg. ProvJGW / PPADoC / AGSec"
                                  value={additionalRank}
                                  onChange={(e) => setAdditionalRank(e.target.value)}
                                />
                              </div>
                            </div>
                            {!hasConfiguredPrefixes && (
                              <p className="text-xs text-amber-600">
                                No prefixes are configured for this unit type. Please contact the event organizer.
                              </p>
                            )}
                          </div>
                        )}

                        {currentUser && lockPersonalDetailsFromAccount ? (
                          <>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                              <div className="space-y-2">
                                <Label>First Name</Label>
                                <div className="rounded-md border border-input bg-muted px-3 py-2 text-sm">
                                  {firstName}
                                </div>
                              </div>
                              <div className="space-y-2">
                                <Label>Last Name</Label>
                                <div className="rounded-md border border-input bg-muted px-3 py-2 text-sm">
                                  {lastName}
                                </div>
                              </div>
                            </div>

                            {requireAffiliationInfo && (
                              <div className="space-y-2">
                                <Label htmlFor="unit">
                                  {unitLabel}{' '}
                                  <span className="font-normal text-muted-foreground">(optional)</span>
                                </Label>
                                <Input
                                  id="unit"
                                  type="text"
                                  placeholder={
                                    currentUnitType
                                      ? `${currentUnitType.entityName} name`
                                      : 'Veritas Unit No. 1234'
                                  }
                                  value={unitName}
                                  onChange={(e) => setUnitName(e.target.value)}
                                />
                              </div>
                            )}

                            <div className="space-y-2">
                              <Label>Email Address</Label>
                              <div className="rounded-md border border-input bg-muted px-3 py-2 text-sm">
                                {email}
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                              <div className="space-y-2">
                                <Label htmlFor="firstName">First Name *</Label>
                                <Input
                                  id="firstName"
                                  type="text"
                                  placeholder="John"
                                  value={firstName}
                                  onChange={(e) => setFirstName(e.target.value)}
                                  required
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="lastName">Last Name *</Label>
                                <Input
                                  id="lastName"
                                  type="text"
                                  placeholder="Smith"
                                  value={lastName}
                                  onChange={(e) => setLastName(e.target.value)}
                                  required
                                />
                              </div>
                            </div>

                            {requireAffiliationInfo && (
                              <div className="space-y-2">
                                <Label htmlFor="unit">
                                  {unitLabel}{' '}
                                  <span className="font-normal text-muted-foreground">(optional)</span>
                                </Label>
                                <Input
                                  id="unit"
                                  type="text"
                                  placeholder={
                                    currentUnitType
                                      ? `${currentUnitType.entityName} name`
                                      : 'Veritas Unit No. 1234'
                                  }
                                  value={unitName}
                                  onChange={(e) => setUnitName(e.target.value)}
                                />
                              </div>
                            )}

                            <div className="space-y-2">
                              <Label htmlFor="email">Email Address *</Label>
                              <Input
                                id="email"
                                type="email"
                                placeholder="john@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onBlur={handleEmailBlur}
                                required
                              />
                              {emailHasAccount && !currentUser && (
                                <p className="text-xs text-amber-600">
                                  This email is already registered. Please log in below to continue.
                                </p>
                              )}
                            </div>

                            {emailHasAccount && !currentUser && (
                              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3">
                                <div className="flex items-center gap-2">
                                  <div className="h-2 w-2 rounded-full bg-amber-600" />
                                  <p className="text-sm font-semibold text-amber-900">Login Required</p>
                                </div>
                                <p className="text-sm text-amber-800">
                                  This email address is associated with an existing account. Please log in to continue with your RSVP.
                                </p>
                                <div className="space-y-2">
                                  <Label htmlFor="loginPassword">Password</Label>
                                  <Input
                                    id="loginPassword"
                                    type="password"
                                    placeholder="Enter your password"
                                    value={loginPassword}
                                    onChange={(e) => setLoginPassword(e.target.value)}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleLogin();
                                      }
                                    }}
                                  />
                                </div>
                                <Button
                                  type="button"
                                  onClick={handleLogin}
                                  className="w-full"
                                  variant="default"
                                >
                                  Log In
                                </Button>
                                <p className="text-xs text-center text-amber-700">
                                  <button
                                    type="button"
                                    onClick={() => navigate('/reset-password', { state: { email } })}
                                    className="underline hover:text-amber-900"
                                  >
                                    Forgot password?
                                  </button>
                                </p>
                              </div>
                            )}
                          </>
                        )}
                      </CardContent>
                    </Card>

                    {/* Ticket & Meal Preferences Card */}
                    {availableTickets.length > 0 ? (
                      <Card>
                        <CardHeader>
                          <CardTitle>Ticket & Meal Preferences</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="ticketType">Ticket Type *</Label>
                            <Select value={selectedTicketId} onValueChange={setSelectedTicketId}>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a ticket type..." />
                              </SelectTrigger>
                              <SelectContent>
                                {availableTickets.map((t) => (
                                  <SelectItem key={t.id} value={t.id}>
                                    {t.name} - {formatCurrency(t.price || 0)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Course selections for selected ticket */}
                          {selectedTicketId && (() => {
                            const selectedTicket = availableTickets.find(t => t.id === selectedTicketId);
                            if (!selectedTicket?.courses || selectedTicket.courses.length === 0) return null;
                            
                            return (
                              <div className="space-y-4">
                                <h4 className="text-md font-medium">Meal Choices</h4>
                                {selectedTicket.courses.map((c) => (
                                  <div key={c.id} className="space-y-2">
                                    <Label>{c.name} *</Label>
                                    <Select
                                      value={ticketCourseSelections[c.id] || ''}
                                      onValueChange={(value) => {
                                        setTicketCourseSelections({
                                          ...ticketCourseSelections,
                                          [c.id]: value,
                                        });
                                      }}
                                    >
                                      <SelectTrigger>
                                        <SelectValue placeholder="Select your choice..." />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {(c.options || []).map((option) => (
                                          <SelectItem key={option} value={option}>
                                            {option}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                ))}
                              </div>
                            );
                          })()}

                          <div className="space-y-2">
                            <Label htmlFor="allergies">Allergies & Special Requirements</Label>
                            <Textarea
                              id="allergies"
                              placeholder="Please list any allergies or special dietary requirements..."
                              rows={3}
                              value={allergies}
                              onChange={(e) => setAllergies(e.target.value)}
                            />
                            <p className="text-xs text-muted-foreground">
                              This information will be shared with the kitchen staff to ensure your safety
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    ) : null}

                    {/* Additional Questions Card */}
                    {!isInterestOnly && event?.questionnaire && event.questionnaire.length > 0 && (
                      <Card>
                        <CardHeader>
                          <CardTitle>Additional Questions</CardTitle>
                          <CardDescription>
                            Please answer the following questions
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {event.questionnaire.map((question) => (
                            <div key={question.id} className="space-y-2">
                              <Label htmlFor={`question-${question.id}`}>
                                {question.question}
                                {question.required && <span className="text-destructive ml-1">*</span>}
                              </Label>
                              {question.type === 'text' && (
                                <Input
                                  id={`question-${question.id}`}
                                  type="text"
                                  placeholder="Your answer"
                                  value={(questionnaireResponses[question.id] as string) || ''}
                                  onChange={(e) =>
                                    setQuestionnaireResponses({
                                      ...questionnaireResponses,
                                      [question.id]: e.target.value,
                                    })
                                  }
                                  required={question.required}
                                />
                              )}
                              {question.type === 'textarea' && (
                                <Textarea
                                  id={`question-${question.id}`}
                                  placeholder="Your answer"
                                  rows={4}
                                  value={(questionnaireResponses[question.id] as string) || ''}
                                  onChange={(e) =>
                                    setQuestionnaireResponses({
                                      ...questionnaireResponses,
                                      [question.id]: e.target.value,
                                    })
                                  }
                                  required={question.required}
                                />
                              )}
                              {question.type === 'select' && (
                                <Select
                                  value={(questionnaireResponses[question.id] as string) || ''}
                                  onValueChange={(value) =>
                                    setQuestionnaireResponses({
                                      ...questionnaireResponses,
                                      [question.id]: value,
                                    })
                                  }
                                  required={question.required}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select an option..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {question.options?.map((option) => (
                                      <SelectItem key={option} value={option}>
                                        {option}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                              {question.type === 'multiselect' && (
                                <div className="space-y-2">
                                  {question.options?.map((option) => {
                                    const currentValues = (questionnaireResponses[question.id] as string[]) || [];
                                    const isSelected = currentValues.includes(option);
                                    return (
                                      <label
                                        key={option}
                                        className="flex items-center space-x-2 cursor-pointer"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={(e) => {
                                            const currentValues = (questionnaireResponses[question.id] as string[]) || [];
                                            if (e.target.checked) {
                                              setQuestionnaireResponses({
                                                ...questionnaireResponses,
                                                [question.id]: [...currentValues, option],
                                              });
                                            } else {
                                              setQuestionnaireResponses({
                                                ...questionnaireResponses,
                                                [question.id]: currentValues.filter((v) => v !== option),
                                              });
                                            }
                                          }}
                                          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                        />
                                        <span className="text-sm">{option}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    )}

                    {/* Future Events Card */}
                    {allowFutureEventsInterest && (
                      <Card>
                        <CardContent className="pt-6">
                          <label className="flex items-start space-x-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={futureEvents}
                              onChange={(e) => setFutureEvents(e.target.checked)}
                              className="mt-1 rounded border-gray-300"
                            />
                            <div>
                              <div className="font-semibold">Invite me to future events</div>
                              <div className="text-sm text-muted-foreground">
                                Receive invitations for upcoming festive boards and special events
                              </div>
                            </div>
                          </label>
                        </CardContent>
                      </Card>
                    )}

                {/* Payment Summary */}
                {availableTickets.length > 0 && selectedTicketId ? (
                  <div className="rounded-lg bg-muted p-4">
                    <div className="space-y-2 mb-3">
                      {(() => {
                        const selectedTicket = availableTickets.find(t => t.id === selectedTicketId);
                        if (!selectedTicket) return null;
                        const ticketPrice = selectedTicket.price || 0;
                        const recoveryPounds = getInvoiceCreditRecoveryPounds(event, ticketPrice, creditBookingFeePence);
                        const showBookingFeeRow = shouldShowBookingFeeOnRsvp(
                          event,
                          ticketPrice,
                          inviterPaysForGuest,
                          creditBookingFeePence,
                        );
                        return (
                          <>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">{selectedTicket.name}</span>
                              <span className="font-medium">{formatCurrency(ticketPrice)}</span>
                            </div>
                            {showBookingFeeRow ? (
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-muted-foreground">{BOOKING_FEE_LABEL}</span>
                                <span className="font-medium">{formatCurrency(recoveryPounds)}</span>
                              </div>
                            ) : null}
                          </>
                        );
                      })()}
                      <div className="border-t pt-2 flex items-center justify-between">
                        <span className="font-semibold">{inviterPaysForGuest ? 'Paid by inviter:' : 'Total Amount:'}</span>
                        {inviterPaysForGuest ? (
                          <span className="text-lg font-bold text-green-700">Covered for you</span>
                        ) : (
                          <span className="text-2xl font-bold">
                            {(() => {
                              const selectedTicket = availableTickets.find(t => t.id === selectedTicketId);
                              const ticketPrice = selectedTicket?.price || 0;
                              const recoveryPounds = getInvoiceCreditRecoveryPounds(event, ticketPrice, creditBookingFeePence);
                              return formatCurrency(ticketPrice + recoveryPounds);
                            })()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1 text-sm text-muted-foreground">
                      <p>
                        RSVP deadline: {formatDate(event.rsvpDeadline)}
                      </p>
                      {!inviterPaysForGuest && (
                        <p>
                          Payment deadline: {formatDate(event.paymentDeadline || event.rsvpDeadline)}
                        </p>
                      )}
                    </div>
                  </div>
                ) : null}

                {/* Add-guests hint */}
                {!invitedBy && (
                  <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
                    <Users className="h-5 w-5 shrink-0 text-blue-600 mt-0.5" />
                    <p className="text-sm text-blue-900">
                      Once you submit your RSVP, you'll be able to add guests to your booking.
                    </p>
                  </div>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  className="w-full"
                  size="lg"
                  disabled={submitting || (emailHasAccount && !currentUser) || isEventLocked}
                >
                  {submissionType === 'standard' && submitting
                    ? 'Submitting...'
                    : emailHasAccount && !currentUser
                      ? 'Please Log In First'
                      : inviterPaysForGuest
                        ? 'Submit RSVP (Paid by inviter)'
                        : 'Submit RSVP'}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full"
                  size="lg"
                  disabled={submitting || isEventLocked}
                  onClick={handleApologySubmit}
                >
                  {submissionType === 'apology' && submitting ? 'Recording...' : 'Record my apologies'}
                </Button>

                <p className="text-center text-xs text-muted-foreground">
                  By continuing, you agree to our Terms of Service and Privacy Policy
                </p>
                </>
                )}
              </form>
            </>
          )}
        </div>
      </main>

      {/* Share Interest Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Share Interest Registration</DialogTitle>
            <DialogDescription>
              Send this event's interest registration link to someone via email
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="share-name">Recipient's Name *</Label>
              <Input
                id="share-name"
                type="text"
                placeholder="John Smith"
                value={shareName}
                onChange={(e) => setShareName(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="share-email">Recipient's Email *</Label>
              <Input
                id="share-email"
                type="email"
                placeholder="john@example.com"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                required
              />
            </div>

            <div className="rounded-lg bg-blue-50 p-3 border border-blue-200">
              <p className="text-sm text-blue-800">
                They will receive an email with a link to register their interest for {event?.name}.
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowShareDialog(false);
                  setShareEmail('');
                  setShareName('');
                }}
                disabled={sharing}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={handleShareInterest}
                disabled={sharing || !shareEmail.trim() || !shareName.trim()}
              >
                {sharing ? 'Sending...' : 'Send Email'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </EventPageBackground>
  );
}

